import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { UserStatus } from '../../services/api';
import UserMenu from './UserMenu';
import './HeaderActions.css';

interface HeaderActionsProps {
    userStatus: UserStatus | null;
    onShowLogin: () => void;
}

interface Language {
    code: string;
    name: string;
    nativeName: string;
    flag: string;
}

const LANGUAGES: Language[] = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
    { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
    // Easy to add more:
    // { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
    // { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
];

export default function HeaderActions({ userStatus, onShowLogin }: HeaderActionsProps) {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [showLangModal, setShowLangModal] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const isRo = i18n.language.startsWith('ro');
    const currentLang = LANGUAGES.find(l => l.code === (isRo ? 'ro' : 'en')) || LANGUAGES[0];

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowLangModal(false);
            }
        };
        if (showLangModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showLangModal]);

    const changeLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
        setShowLangModal(false);

        // Update URL based on language
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const urlLang = ['ro', 'en'].includes(pathSegments[0]) ? pathSegments[0] : null;

        let newPath = '';
        if (lang === 'en') {
            newPath = urlLang ? '/' + pathSegments.slice(1).join('/') : location.pathname;
        } else {
            newPath = urlLang ? '/' + lang + '/' + pathSegments.slice(1).join('/') : '/' + lang + location.pathname;
        }

        newPath = newPath.replace(/\/+/g, '/');
        if (newPath === '') newPath = '/';

        navigate({ to: newPath } as any);
    };

    return (
        <div className="header-actions-pills">
            <div className="pill-group">
                <button className="header-pill">{isRo ? 'RON' : '€'}</button>
                <div className="lang-pill-wrapper" ref={dropdownRef}>
                    <button 
                        className={`header-pill lang-pill ${showLangModal ? 'active' : ''}`}
                        onClick={() => setShowLangModal(!showLangModal)}
                        aria-label={t('common.change_language', 'Change language')}
                    >
                        {currentLang.flag}
                    </button>
                    {showLangModal && (
                        <div className="lang-dropdown">
                            <div className="lang-dropdown-header">
                                {t('common.select_language', 'Select Language')}
                            </div>
                            <div className="lang-options">
                                {LANGUAGES.map((lang) => (
                                    <button
                                        key={lang.code}
                                        className={`lang-option ${lang.code === (isRo ? 'ro' : 'en') ? 'active' : ''}`}
                                        onClick={() => changeLanguage(lang.code)}
                                    >
                                        <span className="lang-option-flag">{lang.flag}</span>
                                        <div className="lang-option-text">
                                            <span className="lang-option-native">{lang.nativeName}</span>
                                            <span className="lang-option-name">{lang.name}</span>
                                        </div>
                                        {lang.code === (isRo ? 'ro' : 'en') && <CheckIcon />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <button className="header-pill">°C</button>
            </div>

            <UserMenu
                userStatus={userStatus}
                onShowLogin={onShowLogin}
            />
        </div>
    );
}

const CheckIcon = () => (
    <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);
